/**
 * <Page /> — the <main> landmark and the one <h1>, for every content page.
 *
 * WHY THIS EXISTS
 *   A page that forgets its <h1>, or grows a second one, reads as a broken
 *   document to a screen reader and to an answer engine. Task 6 solved the
 *   same problem for the <head> with one table. This is the same idea for
 *   the body: the right thing is the easy thing, because there is nothing
 *   to remember.
 *
 * WHAT IT DOES NOT DO
 *   It does not write the <head>. <Seo /> owns that, and two ways to set
 *   one thing is worse than one. It takes no other props until a second
 *   page actually needs one.
 *
 *   It renders no header and no footer either. A page places those itself,
 *   as siblings of this <main>, which is what makes all three real
 *   landmarks. See docs/aeo/AEO-LEDGER.md, task 9.
 */

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { colors, headingStyles, mobile, tablet } from '../styles';

const Main = styled.main`
  width: 100%;
`;

const Title = styled.h1`
  ${headingStyles}
  color: ${colors.defaultText};
  font-size: 34px;
  line-height: 1.25;
  margin: 0 0 8px;

  @media (max-width: ${tablet}) {
    font-size: 30px;
  }

  @media (max-width: ${mobile}) {
    font-size: 26px;
  }
`;

const Page = ({ title, children }) => (
  <Main>
    <Title>{title}</Title>
    {children}
  </Main>
);

Page.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node
};

export default Page;
